import { type ApiClient, useSingleAttemptApiClient } from '@vrplatform/data';
import { useCallback } from 'react';
import {
  downloadBlob,
  downloadFromUrl,
  type FileTransferDisposition,
} from '../../utils/download';
import {
  type AsyncExportResult,
  type StartAsyncExport,
  useAsyncExport,
} from './useAsyncExport';

type AwaitedArtifact = Readonly<{
  url: string;
}>;

type AwaitArtifact<TSelector> = (input: {
  api: ApiClient;
  selector: TSelector;
  signal: AbortSignal;
}) => Promise<AwaitedArtifact>;

type RunArtifactExportInput<TSelector> = Readonly<{
  awaitResult: AwaitArtifact<TSelector>;
  exportKey: string;
  fallbackFilename: string;
  // Skips the awaited GET for selectors that cannot be bounded to a date range.
  queueOnly?: boolean;
  queue: StartAsyncExport<TSelector>;
  selector: TSelector;
  transfer?: FileTransferDisposition | 'none';
}>;

export type ArtifactExportResult =
  | (AwaitedArtifact & { delivery: 'awaited' })
  | (AsyncExportResult & { delivery: 'durable' });

export const useArtifactExport = () => {
  // A retried GET would outlive the 60 s abort below and skip the POST fallback.
  const api = useSingleAttemptApiClient();
  const { runExport: runAsyncExport } = useAsyncExport();

  const runExport = useCallback(
    async <TSelector>({
      awaitResult,
      exportKey,
      fallbackFilename,
      queue,
      queueOnly = false,
      selector,
      transfer = 'download',
    }: RunArtifactExportInput<TSelector>): Promise<ArtifactExportResult> => {
      if (!queueOnly) {
        try {
          const result = await awaitResult({
            api,
            selector,
            signal: AbortSignal.timeout(60_000),
          });

          if (transfer !== 'none') {
            const url = new URL(result.url);
            // Signed artifact links download by default; the API renders PDFs
            // inline only when the link asks for it.
            if (transfer === 'openInNewTab') {
              url.searchParams.set('disposition', 'inline');
            }
            downloadFromUrl(url.toString(), fallbackFilename, {
              disposition: transfer,
            });
          }

          return { ...result, delivery: 'awaited' };
        } catch (error) {
          if (
            !error ||
            typeof error !== 'object' ||
            !(
              ('code' in error && error.code === 'EXPORT_REQUIRES_POST') ||
              // A GET that outlived its wait returns 504 with the same hint.
              ('context' in error &&
                !!error.context &&
                typeof error.context === 'object' &&
                'recommendedMethod' in error.context &&
                error.context.recommendedMethod === 'POST')
            )
          ) {
            throw error;
          }
        }
      }

      const result = await runAsyncExport({
        body: selector,
        exportKey,
        fallbackFilename,
        start: queue,
        transfer: 'none',
      });

      if (transfer !== 'none') {
        downloadBlob(result.blob, result.filename, {
          disposition: transfer,
        });
      }

      return { ...result, delivery: 'durable' };
    },
    [api, runAsyncExport]
  );

  return { runExport };
};
